import { ConnectWallet } from "@bkry/bowline-components/ConnectWallet";
import { genericAbiRead } from "@bkry/bowline-redux/contracts";
import { useWeb3React } from "@web3-react/core";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import UserLootbox from "../UserLootbox";
import UserLootboxChip from "../UserLootboxChip";
import {
  attachingEnabledAbi,
  claimingPhaseEnabledAbi,
  minAttachTimeAbi,
} from "../lootboxAbiMethods";

const {
  REACT_APP_GENERATOR_CONTRACT_ID,
  REACT_APP_GEN3_CHIP_CONTRACT_ID,
  REACT_APP_GENERATOR_CLAIM_PHASE,
} = typeof window !== "undefined" && window.env ? window.env : process.env;

const UserLootboxes = (
  { setHideGenericModals, useMetadata, getTokens },
  context
) => {
  const [activeTab, setActiveTab] = useState("1");
  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const { account, provider } = useWeb3React();
  const dispatch = useDispatch();

  const [claimingEnabled, setClaimingEnabled] = useState(false);
  const [attachingEnabled, setAttachingEnabled] = useState(false);
  const [minAttachTime, setMinAttachTime] = useState(86400);

  useEffect(() => {
    if (account) getTokens();
  }, [account]);

  const generatorIds = useSelector(
    (state) =>
      state.tokens.contractsToTokenIdsDictionary?.[
        REACT_APP_GENERATOR_CONTRACT_ID
      ]
  );
  const chipIds = useSelector(
    (state) =>
      state.tokens.contractsToTokenIdsDictionary?.[
        REACT_APP_GEN3_CHIP_CONTRACT_ID
      ]
  );

  const getClaimingPhaseEnabled = async (phase) => {
    if (useMetadata) return false;
    if (!account) return false;
    if (!claimingPhaseEnabledAbi?.name) return false;
    if (!REACT_APP_GENERATOR_CONTRACT_ID.split(",").length) return false;
    dispatch(
      genericAbiRead({
        functionAbi: claimingPhaseEnabledAbi,
        contractAddress: REACT_APP_GENERATOR_CONTRACT_ID.split(",")[0],
        account: account,
        provider: provider,
        genericInputs: {
          tokenIdentifier: phase, // we are passing a phase even though we call it tokenIdentifier
        },
        successCallbackFnc: (data) => {
          setClaimingEnabled(data);
        },
        errorCallbackFnc: (data) => {
          console.log("claim enabled error", data);
        },
      })
    );
  };

  const getMinAttachTime = async () => {
    if (useMetadata) return false;
    if (!account) return false;
    if (!minAttachTimeAbi?.name) return false;
    if (!REACT_APP_GENERATOR_CONTRACT_ID.split(",").length) return false;
    dispatch(
      genericAbiRead({
        functionAbi: minAttachTimeAbi,
        contractAddress: REACT_APP_GENERATOR_CONTRACT_ID.split(",")[0],
        account: account,
        provider: provider,
        successCallbackFnc: (data) => {
          if (data?.toNumber) setMinAttachTime(data.toNumber());
        },
        errorCallbackFnc: (data) => {
          console.log("min attach time error", data);
        },
      })
    );
  };

  const getAttachingEnabled = async () => {
    if (useMetadata) return false;
    if (!account) return false;
    if (!attachingEnabledAbi?.name) return false;
    if (!REACT_APP_GENERATOR_CONTRACT_ID.split(",").length) return false;
    dispatch(
      genericAbiRead({
        functionAbi: attachingEnabledAbi,
        contractAddress: REACT_APP_GENERATOR_CONTRACT_ID.split(",")[0],
        account: account,
        provider: provider,
        successCallbackFnc: (data) => {
          setAttachingEnabled(data);
        },
        errorCallbackFnc: (data) => {
          console.log("attaching enabled error", data);
        },
      })
    );
  };

  useEffect(() => {
    if (!useMetadata) {
      getAttachingEnabled();
      getClaimingPhaseEnabled(REACT_APP_GENERATOR_CLAIM_PHASE);
      getMinAttachTime();
      const interval = setInterval(() => {
        getAttachingEnabled();
        getClaimingPhaseEnabled(REACT_APP_GENERATOR_CLAIM_PHASE);
      }, 60000); //refresh every minute
      return () => clearInterval(interval);
    }
  }, [account, useMetadata]);

  return (
    <Container>
      <h2 className="text-center mb-5">Your Inventory</h2>
      {account ? (
        <>
          <Nav
            tabs
            className="d-flex justify-content-center border-bottom-0 mb-5"
          >
            <NavItem className="mx-1 mx-lg-3 my-2 my-lg-0">
              <NavLink
                className={
                  activeTab === "1"
                    ? "active text-white bg-secondary btn-outline-primary border rounded-pill"
                    : "cursor-pointer btn-outline-primary border rounded-pill"
                }
                onClick={() => toggleTab("1")}
              >
                GEN3RATORs
              </NavLink>
            </NavItem>
            <NavItem className="mx-1 mx-lg-3 my-2 my-lg-0">
              <NavLink
                className={
                  activeTab === "2"
                    ? "active text-white bg-secondary btn-outline-primary border rounded-pill"
                    : "cursor-pointer btn-outline-primary border rounded-pill"
                }
                onClick={() => toggleTab("2")}
              >
                GEN3 Chips
              </NavLink>
            </NavItem>
          </Nav>
          <TabContent activeTab={activeTab} className="mb-3">
            <TabPane tabId="1">
              <p className="text-center mb-5">
                {generatorIds?.length > 0
                  ? context.t("Manage your GEN3RATORs and attached GEN3 Chips")
                  : context.t("You don't have any GEN3RATORs yet")}
              </p>
              {!account && (
                <div className="text-center">
                  <ConnectWallet buttonColor="primary" buttonSize="xl" />
                </div>
              )}
              <Row>
                {generatorIds?.map((genId) => (
                  <Col key={genId} md={6} className="mb-5">
                    <UserLootbox
                      lootBoxId={genId}
                      chipIds={chipIds}
                      claimingEnabled={claimingEnabled}
                      attachingEnabled={attachingEnabled}
                      getTokens={getTokens}
                      chipsContractAddress={
                        REACT_APP_GEN3_CHIP_CONTRACT_ID.split(",").length
                          ? REACT_APP_GEN3_CHIP_CONTRACT_ID.split(",")[0]
                          : ""
                      }
                      generatorContractAddress={
                        REACT_APP_GENERATOR_CONTRACT_ID.split(",").length
                          ? REACT_APP_GENERATOR_CONTRACT_ID.split(",")[0]
                          : ""
                      }
                      setHideGenericModals={setHideGenericModals}
                      useMetadata={useMetadata}
                      minAttachTime={minAttachTime}
                    />
                  </Col>
                ))}
              </Row>
            </TabPane>
            <TabPane tabId="2">
              {chipIds?.length <= 0 && (
                <p className="text-center mb-5">
                  {context.t("You don't own any unattached GEN3 Chips")}
                </p>
              )}
              <Row>
                {chipIds?.map((chipId) => (
                  <Col key={chipId} md={3} xs={6} className="mb-3">
                    <UserLootboxChip chipId={chipId} />
                  </Col>
                ))}
              </Row>
            </TabPane>
          </TabContent>
        </>
      ) : (
        <>
          <p className="text-center mb-5">
            {context.t(
              "Connect your wallet to view your GEN3RATORs and GEN3 Chips"
            )}
          </p>
          <div className="text-center">
            <ConnectWallet buttonColor="primary" buttonSize="xl" />
          </div>
        </>
      )}
    </Container>
  );
};

UserLootboxes.contextTypes = {
  t: PropTypes.func,
};

UserLootboxes.propTypes = {
  setHideGenericModals: PropTypes.func.isRequired,
  useMetadata: PropTypes.bool,
  getTokens: PropTypes.func.isRequired,
};

UserLootboxes.defaultProps = {
  useMetadata: false,
};

export default UserLootboxes;
